import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { postData, getFileUrl } from '../../Utils/Server';
import Endpoints from '../../Constants/Endpoints';
import { ReasonCaptureModal } from '../Common/ReasonCaptureModal';
import PageLayout from '../Layout/PageLayout';
import { Card, CardTitleBar, CardBody, Button, StatusBadge, TextArea, Spinner, tokens } from '../UI';

export default function Review() {
  const { id } = useParams();
  const history = useHistory();
  const [entry, setEntry] = useState(null); // either Rider or {rider, *_plain}
  const [pending, setPending] = useState(null); // 'approve' | 'reject' | null

  const load = useCallback(() => {
    // Load the rider by id (with decrypted doc URLs) rather than scanning the
    // pending list — so the page works for pending, approved and rejected riders.
    const fd = new FormData();
    fd.append('rider_id', id);
    postData(Endpoints.RIDER_VETTING_DETAIL, fd)
      .then((res) => res.json())
      .then((res) => setEntry(res));
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  if (!entry) return <Spinner center label="Loading…" />;

  const rider = entry.rider || entry;
  // Decrypted values are bare filenames — resolve to a full /files/ URL to view.
  const dlURL = entry.dl_doc_url_plain ? getFileUrl(entry.dl_doc_url_plain) : null;
  const gcURL = entry.good_conduct_url_plain ? getFileUrl(entry.good_conduct_url_plain) : null;
  const nhifURL = entry.nhif_nssf_url_plain ? getFileUrl(entry.nhif_nssf_url_plain) : null;

  const submit = (reason) => {
    const fd = new FormData();
    fd.append('rider_id', rider.id);
    fd.append('reason', reason);
    const endpoint =
      pending === 'approve'
        ? Endpoints.APPROVE_RIDER_VETTING
        : Endpoints.REJECT_RIDER_VETTING;
    postData(endpoint, fd)
      .then((res) => res.json())
      .then((updated) => {
        // updated is either the rider directly or {rider, *_plain}
        const updatedRider = updated.rider || updated;
        setPending(null);
        if (updatedRider.id) {
          // Vetting done — the rider leaves the pending list, so return to it.
          // (Staying here would re-query pending, not find them, and spin forever.)
          history.push('/rider-vetting');
        }
      });
  };

  const uploadFile = (kind, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('rider_id', rider.id);
    fd.append('kind', kind);
    fd.append('file', file);
    fd.append('reason', 'document upload');
    postData(Endpoints.UPLOAD_RIDER_DOC, fd)
      .then((res) => res.json())
      .then(() => load());
  };

  const uploadNextOfKin = (json) => {
    const fd = new FormData();
    fd.append('rider_id', rider.id);
    fd.append('kind', 'next_of_kin');
    fd.append('next_of_kin_json', json);
    fd.append('reason', 'next of kin update');
    postData(Endpoints.UPLOAD_RIDER_DOC, fd)
      .then((res) => res.json())
      .then(() => load());
  };

  const status = rider.vetting_status || 'pending';

  return (
    <PageLayout title={`${rider.name} — vetting review`}>
      <Card style={{ marginBottom: tokens.spacing[4], maxWidth: 720 }}>
        <div style={{ marginBottom: tokens.spacing[3], color: tokens.colors.body }}>
          Phone: {rider.phone}
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: tokens.spacing[2],
            color: tokens.colors.body,
          }}
        >
          Status: <StatusBadge status={status}>{status}</StatusBadge>
        </div>
        {rider.rejection_reason && (
          <div style={{ marginTop: tokens.spacing[3], color: tokens.colors.muted }}>
            Previous rejection reason: {rider.rejection_reason}
          </div>
        )}
      </Card>

      <Card padded={false} style={{ marginBottom: tokens.spacing[4], maxWidth: 720 }}>
        <CardTitleBar title="Documents" />
        <CardBody>
          <ul style={{ margin: 0, paddingLeft: tokens.spacing[5], color: tokens.colors.body }}>
            <li style={{ marginBottom: tokens.spacing[3] }}>
              Driving license:{' '}
              {dlURL ? (
                <a href={dlURL} target="_blank" rel="noreferrer">
                  view
                </a>
              ) : rider.dl_doc_url ? (
                '(uploaded, no permission to view)'
              ) : (
                'not uploaded'
              )}
              {!dlURL && !rider.dl_doc_url && (
                <input
                  type="file"
                  onChange={(e) => uploadFile('dl', e.target.files[0])}
                />
              )}
            </li>
            <li style={{ marginBottom: tokens.spacing[3] }}>
              Good Conduct:{' '}
              {gcURL ? (
                <a href={gcURL} target="_blank" rel="noreferrer">
                  view
                </a>
              ) : rider.good_conduct_url ? (
                '(uploaded, no permission to view)'
              ) : (
                'not uploaded'
              )}
              {!gcURL && !rider.good_conduct_url && (
                <input
                  type="file"
                  onChange={(e) => uploadFile('good_conduct', e.target.files[0])}
                />
              )}
            </li>
            <li style={{ marginBottom: tokens.spacing[3] }}>
              NHIF/NSSF:{' '}
              {nhifURL ? (
                <a href={nhifURL} target="_blank" rel="noreferrer">
                  view
                </a>
              ) : rider.nhif_nssf_url ? (
                '(uploaded, no permission to view)'
              ) : (
                'not uploaded'
              )}
              {!nhifURL && !rider.nhif_nssf_url && (
                <input
                  type="file"
                  onChange={(e) => uploadFile('nhif_nssf', e.target.files[0])}
                />
              )}
            </li>
            <li>
              Next of Kin:{' '}
              {(() => {
                if (!rider.next_of_kin_json) {
                  return <span style={{color: tokens.colors.muted}}>not provided</span>;
                }
                let nok = null;
                try { nok = JSON.parse(rider.next_of_kin_json); } catch (e) { nok = null; }
                if (!nok || typeof nok !== 'object') {
                  return <span>{rider.next_of_kin_json}</span>;
                }
                return (
                  <span>
                    <strong>{nok.name || '—'}</strong>
                    {nok.relationship ? ` · ${nok.relationship}` : ''}
                    {nok.phone ? ` · ${nok.phone}` : ''}
                  </span>
                );
              })()}
              {!rider.next_of_kin_json && (
                <div>
                  <TextArea
                    placeholder='{"name":"...","phone":"...","relationship":"..."}'
                    rows={3}
                    onBlur={(e) => {
                      if (e.target.value.trim()) uploadNextOfKin(e.target.value);
                    }}
                  />
                </div>
              )}
            </li>
          </ul>
        </CardBody>
      </Card>

      <div style={{ display: 'flex', gap: tokens.spacing[2] }}>
        <Button
          onClick={() => setPending('approve')}
          disabled={rider.vetting_status === 'approved'}
        >
          Approve
        </Button>
        <Button
          variant="danger"
          onClick={() => setPending('reject')}
          disabled={rider.vetting_status === 'rejected'}
        >
          Reject
        </Button>
      </div>

      {pending && (
        <ReasonCaptureModal
          title={pending === 'approve' ? 'Approve rider' : 'Reject rider'}
          onCancel={() => setPending(null)}
          onConfirm={submit}
        />
      )}
    </PageLayout>
  );
}
